"use client";

import { useState, useEffect } from "react";
import { 
  Users, 
  Calendar, 
  Award, 
  TrendingUp, 
  Search, 
  Filter, 
  RefreshCw, 
  Briefcase, 
  CheckCircle2, 
  Layers,
  Plus
} from "lucide-react";
import { CandidateLead } from "@/lib/demos/hiringDb";
import { EXECUTIVE_PRACTICES } from "@/lib/demos/hiringData";
import { HiringTopBar } from "./HiringTopBar";
import { HiringKanban } from "./HiringKanban";
import { CandidateDetailModal } from "./CandidateDetailModal";
import { CandidateStageModal } from "./CandidateStageModal";
import { RequisitionProposalModal } from "./RequisitionProposalModal";

interface HiringCrmDashboardProps {
  initialCurrency?: "INR" | "USD";
}

export function HiringCrmDashboard({ initialCurrency = "INR" }: HiringCrmDashboardProps) {
  const [currency, setCurrency] = useState<"INR" | "USD">(initialCurrency);
  const [candidates, setCandidates] = useState<CandidateLead[]>([]);
  const [isResetting, setIsResetting] = useState(false);
  const [stats, setStats] = useState<any>({
    totalCandidates: 0,
    interviewsScheduled: 0,
    placementsWon: 0,
    totalPlacementFeeInr: 0,
    totalPlacementFeeUsd: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [practiceFilter, setPracticeFilter] = useState("all");

  // Modals state
  const [selectedCandidate, setSelectedCandidate] = useState<CandidateLead | null>(null);
  const [stageModalData, setStageModalData] = useState<{
    candidate: CandidateLead;
    targetStage: string;
  } | null>(null);
  const [isRequisitionOpen, setIsRequisitionOpen] = useState(false);

  const sym = currency === "USD" ? "$" : "₹";

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setIsLoading(true);
    try {
      const res = await fetch("/api/demos/hiring/candidates");
      const data = await res.json();
      if (data.success) {
        setCandidates(data.candidates || []);
        setStats(data.stats || {});
      }
    } catch (err) {
      console.error("Failed to load hiring candidates", err);
    } finally {
      setIsLoading(false);
    }
  }

  // Direct drag-and-drop update for stages not requiring extra modal inputs
  async function handleDirectStageMove(candidateId: number, targetStage: string) {
    try {
      // Optimistic update
      setCandidates((prev) =>
        prev.map((c) => (c.id === candidateId ? { ...c, stage: targetStage as any } : c))
      );

      const res = await fetch(`/api/demos/hiring/candidates/${candidateId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stage: targetStage }),
      });

      const data = await res.json();
      if (data.success) {
        fetchData();
      }
    } catch (err) {
      console.error("Failed to update candidate stage", err);
      fetchData();
    }
  }

  // Handle stage transitions that require parameters (Interview Round or Placed Won)
  async function handleStageModalConfirm(updates: Partial<CandidateLead>) {
    if (!stageModalData) return;
    const candidateId = stageModalData.candidate.id;

    try {
      const res = await fetch(`/api/demos/hiring/candidates/${candidateId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });

      const data = await res.json();
      if (data.success) {
        setStageModalData(null);
        fetchData();
      }
    } catch (err) {
      console.error("Failed to update candidate with parameters", err);
    }
  }

  const filteredCandidates = candidates.filter((c) => {
    const matchesSearch =
      c.candidate_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.target_role.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.client_company.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.candidate_no.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesPractice =
      practiceFilter === "all" || c.practice_id === practiceFilter;

    return matchesSearch && matchesPractice;
  });

  async function handleResetDemo() {
    setIsResetting(true);
    try {
      await fetch("/api/demos/hiring/reset", { method: "POST" });
      await fetchData();
    } catch (err) {
      console.error("[Reset Demo Error]", err);
    } finally {
      setIsResetting(false);
    }
  }

  const totalFee =
    currency === "USD"
      ? stats.totalPlacementFeeUsd || 0
      : stats.totalPlacementFeeInr || 0;

  return (
    <div className="min-h-screen bg-[#0a0710] flex flex-col">
      <HiringTopBar
        currency={currency}
        onCurrencyToggle={setCurrency}
        onResetDemo={handleResetDemo}
        isResetting={isResetting}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full space-y-6">
        {/* Top KPI Metrics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-5 rounded-2xl bg-[#140c21]/90 border border-purple-900/40 shadow-lg shadow-purple-950/40 flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-400 font-medium">Total Candidates Active</div>
            <div className="text-2xl font-extrabold text-white mt-1">
              {stats.totalCandidates}
            </div>
            <div className="text-[11px] text-purple-400 mt-0.5">Top 1.5% talent pool</div>
          </div>
          <div className="w-11 h-11 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
            <Users className="w-5 h-5" />
          </div>
        </div>

        <div className="p-5 rounded-2xl bg-[#140c21]/90 border border-purple-900/40 shadow-lg shadow-purple-950/40 flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-400 font-medium">Client Interviews Active</div>
            <div className="text-2xl font-extrabold text-white mt-1">
              {stats.interviewsScheduled}
            </div>
            <div className="text-[11px] text-indigo-400 mt-0.5">Evaluation passes issued</div>
          </div>
          <div className="w-11 h-11 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
            <Calendar className="w-5 h-5" />
          </div>
        </div>

        <div className="p-5 rounded-2xl bg-[#140c21]/90 border border-purple-900/40 shadow-lg shadow-purple-950/40 flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-400 font-medium">Placements Closed (Won)</div>
            <div className="text-2xl font-extrabold text-emerald-400 mt-1">
              {stats.placementsWon}
            </div>
            <div className="text-[11px] text-emerald-400 mt-0.5">90-Day guarantee active</div>
          </div>
          <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
            <Award className="w-5 h-5" />
          </div>
        </div>

        <div className="p-5 rounded-2xl bg-[#140c21]/90 border border-purple-900/40 shadow-lg shadow-purple-950/40 flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-400 font-medium">Placement Fee Commission</div>
            <div className="text-xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-indigo-300 mt-1">
              {sym}{totalFee.toLocaleString()}
            </div>
            <div className="text-[11px] text-slate-400 mt-0.5">Net contingency revenue</div>
          </div>
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500/20 to-indigo-500/20 border border-purple-500/30 flex items-center justify-center text-purple-300">
            <TrendingUp className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Control Bar: Search, Filters & Action Button */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 bg-[#140c21]/90 border border-purple-900/40 p-3.5 rounded-2xl">
        <div className="flex flex-wrap items-center gap-2.5 flex-1">
          {/* Search Box */}
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search candidate, role, or client..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#1c1230] border border-purple-900/60 rounded-xl pl-9 pr-3 py-1.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-500"
            />
          </div>

          {/* Practice Filter Pills */}
          <div className="flex items-center gap-1 overflow-x-auto text-xs">
            <button
              type="button"
              onClick={() => setPracticeFilter("all")}
              className={`px-3 py-1 rounded-lg transition-colors ${
                practiceFilter === "all"
                  ? "bg-purple-600 text-white font-semibold"
                  : "text-slate-400 hover:text-white bg-[#1a112c]"
              }`}
            >
              All Practices
            </button>
            {EXECUTIVE_PRACTICES.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPracticeFilter(p.id)}
                className={`px-3 py-1 rounded-lg transition-colors truncate whitespace-nowrap ${
                  practiceFilter === p.id
                    ? "bg-purple-600 text-white font-semibold"
                    : "text-slate-400 hover:text-white bg-[#1a112c]"
                }`}
              >
                {p.title.split(" ")[0]}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={fetchData}
            className="p-2 rounded-xl bg-[#1c1230] hover:bg-[#25183f] text-slate-400 hover:text-white border border-purple-900/60 transition-colors"
            title="Refresh pipeline"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin text-purple-400" : ""}`} />
          </button>
          <button
            type="button"
            onClick={() => setIsRequisitionOpen(true)}
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold text-xs shadow-lg shadow-purple-600/30 flex items-center gap-1.5 transition-all"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>New Requisition</span>
          </button>
        </div>
      </div>

      {/* Kanban Pipeline Board */}
      <HiringKanban
        candidates={filteredCandidates}
        onSelectCandidate={(c) => setSelectedCandidate(c)}
        onRequestStageAdvance={(c, targetStage) =>
          setStageModalData({ candidate: c, targetStage })
        }
        onDirectStageMove={handleDirectStageMove}
        currency={currency}
      />

      {/* Dossier Modal */}
      <CandidateDetailModal
        isOpen={!!selectedCandidate}
        onClose={() => setSelectedCandidate(null)}
        candidate={selectedCandidate}
        onOpenStageModal={(c, targetStage) =>
          setStageModalData({ candidate: c, targetStage })
        }
      />

      {/* Stage Transition Modal */}
      {stageModalData && (
        <CandidateStageModal
          isOpen={!!stageModalData}
          onClose={() => setStageModalData(null)}
          candidate={stageModalData.candidate}
          targetStage={stageModalData.targetStage}
          onConfirm={handleStageModalConfirm}
        />
      )}

      {/* Requisition Proposal Modal */}
      <RequisitionProposalModal
        isOpen={isRequisitionOpen}
        onClose={() => setIsRequisitionOpen(false)}
        currency={currency}
        onSuccess={fetchData}
      />
      </div>
    </div>
  );
}
